import { useCallback, useEffect, useMemo, useState } from "react";
import { Combobox, Listbox, Tag, LegacyStack, Icon } from "@shopify/polaris";
import { SearchIcon } from "@shopify/polaris-icons";
import { useTranslation } from "react-i18next";
import {
  fetchAssignableDepartments,
  AssignableDepartmentsResponse,
} from "services/departments.service";

interface DepartmentOption {
  id: string;
  name: string;
}

interface DepartmentMultiSelectProps {
  companyId: string;
  value: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
  required?: boolean;
  options?: DepartmentOption[];
}

export default function DepartmentMultiSelect({
  companyId,
  value,
  onChange,
  disabled = false,
  required = false,
  options: optionsProp,
}: DepartmentMultiSelectProps) {
  const { t } = useTranslation();
  const [fetchedOptions, setFetchedOptions] = useState<DepartmentOption[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);

  const options = optionsProp ?? fetchedOptions;

  useEffect(() => {
    if (optionsProp || !companyId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data: AssignableDepartmentsResponse =
          await fetchAssignableDepartments(companyId);
        if (!cancelled) {
          setFetchedOptions(data.departments || []);
        }
      } catch {
        if (!cancelled) setFetchedOptions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, optionsProp]);

  const selectedOptions = useMemo(
    () => options.filter((opt) => value.includes(opt.id)),
    [options, value]
  );

  const filteredOptions = useMemo(() => {
    const q = inputValue.trim().toLowerCase();
    if (!q) return options;
    return options.filter((opt) => opt.name.toLowerCase().includes(q));
  }, [inputValue, options]);

  const handleToggle = useCallback(
    (selected: string) => {
      if (!selected || disabled) return;
      onChange(
        value.includes(selected)
          ? value.filter((id) => id !== selected)
          : [...value, selected]
      );
      setInputValue("");
    },
    [disabled, onChange, value]
  );

  const handleRemove = useCallback(
    (id: string) => () => {
      if (disabled) return;
      onChange(value.filter((item) => item !== id));
    },
    [disabled, onChange, value]
  );

  const optionsMarkup = filteredOptions.map((opt) => {
    const isSelected = value.includes(opt.id);
    return (
      <Listbox.Option
        key={opt.id}
        value={opt.id}
        selected={isSelected}
        accessibilityLabel={opt.name}
      >
        <Listbox.TextOption selected={isSelected}>{opt.name}</Listbox.TextOption>
      </Listbox.Option>
    );
  });

  return (
    <div className="department-multi-select">
      <Combobox
        allowMultiple
        activator={
          <Combobox.TextField
            label={t("departments")}
            requiredIndicator={required}
            autoComplete="off"
            prefix={<Icon source={SearchIcon as any} tone="base" />}
            value={inputValue}
            onChange={setInputValue}
            placeholder={
              !optionsProp && loading ? t("loading") : t("select_departments")
            }
            disabled={disabled || (!optionsProp && loading) || !options.length}
            verticalContent={
              selectedOptions.length > 0 ? (
                <LegacyStack spacing="extraTight">
                  {selectedOptions.map((opt) => (
                    <Tag key={opt.id} onRemove={handleRemove(opt.id)}>
                      {opt.name}
                    </Tag>
                  ))}
                </LegacyStack>
              ) : undefined
            }
          />
        }
      >
        {optionsMarkup.length > 0 ? (
          <Listbox onSelect={handleToggle}>{optionsMarkup}</Listbox>
        ) : null}
      </Combobox>
    </div>
  );
}
